import { useState } from "react";
import * as analytics from "@/modules/analytics";
import { isPhoneScale, isIOS } from "@/modules/orientationManager";
import { createImmersiveFullscreen } from "@/components/player/immersiveFullscreen";
import type {
  FullscreenDocument,
  FullscreenElement,
  FullscreenToggle,
  LockableOrientation,
  VideoJsSelf,
} from "@/components/player/videoJsTypes";

// The fullscreen toggle, the document fullscreen mirror, and the fullscreen events.
function createVideoFullscreen(self: VideoJsSelf) {
  const immersive = createImmersiveFullscreen(self);

  // The control-bar fullscreen button drives REAL fullscreen on the whole
  // experience-container (not just the <video>), so the immersive shop buttons +
  // overlays are inside the fullscreen. video.js fullscreens its own el by
  // default; redirect the request to the container and mirror the native
  // fullscreenchange back into the player so its toggle icon/state stay correct.
  // Element fullscreen is supported on desktop, Android, iPadOS 16.4+, and iPhone
  // Safari 17.2+; where a browser lacks it we fall back to video.js's own toggle.
  function setupFullscreen() {
    if (!self.player || !self.player.controlBar) {
      return;
    }
    const doc = document as FullscreenDocument;
    const container = self.player
      .el()
      .closest(".experience-container") as FullscreenElement | null;
    const requestFs =
      container &&
      (container.requestFullscreen ||
        container.webkitRequestFullscreen ||
        container.webkitRequestFullScreen);
    const exitFs = document.exitFullscreen || doc.webkitExitFullscreen;
    const fsToggle = self.player.controlBar.getChild("fullscreenToggle") as
      | FullscreenToggle
      | undefined;

    if (fsToggle) {
      // Capture the toggle's TRUE native handleClick ONCE and stash it on the
      // toggle itself — which persists across rotates with the control bar (the
      // persistent player never recreates it). A re-host must always wrap the
      // native handler, never the previous mount's wrapper, or the wrappers
      // nest unboundedly and every old mount leaks via its retained closure.
      // Guarding on the stash (rather than the live handleClick) keeps this
      // correct even if detachFullscreen's restore is skipped.
      if (!fsToggle._pixNativeHandleClick) {
        fsToggle._pixNativeHandleClick = fsToggle.handleClick.bind(fsToggle);
      }
      const nativeHandleClick = fsToggle._pixNativeHandleClick;
      // handleClick fires via the bound handleClick_ wrapper (dereferenced at
      // call time), so reassigning it wins for both 'tap' and 'click'. The paired
      // tap+click a real touch produces would otherwise enter-then-exit (the
      // fullscreen transition can complete between them), so debounce to one
      // gesture. The request must run inside the gesture — this is one.
      fsToggle.handleClick = event => {
        const now = Date.now();
        if (self.lastFsToggle && now - self.lastFsToggle < 500) {
          return;
        }
        self.lastFsToggle = now;
        // Attribute the resulting fullscreen enter to the button (the event
        // fires on the actual enter in trackFullscreenEnter). Harmless on an
        // exit-click — the method is reset when fullscreen exits.
        self.fsEnterMethod = "button";
        const canRealFs = !!(
          (document.fullscreenEnabled || doc.webkitFullscreenEnabled) &&
          requestFs
        );
        const phoneScaleTouch =
          (isIOS() || (navigator.maxTouchPoints || 0) > 0) && isPhoneScale();
        // iPhone Safari has no element Fullscreen API → CSS pseudo-fullscreen (our
        // UI, force-landscape rotate).
        if (phoneScaleTouch && !canRealFs) {
          immersive.toggleImmersiveFullscreen(container as FullscreenElement);
          return;
        }
        // Real element fullscreen. On an Android phone (phone-scale + real FS) fire
        // it in ANY orientation: video.js's onFullscreenChange locks orientation to
        // landscape, and once rotated the natural landscape render makes the container
        // immersive (our UI) and fills. Deliberately do NOT force window.__pixPseudoFs
        // here — that premature immersive re-render, alongside requestFullscreen +
        // orientation.lock, dropped fullscreen and bounced back to portrait. Desktop /
        // large touch: container fullscreen only makes sense in the immersive-landscape
        // layout; otherwise defer to video.js's default toggle (fixed-pixel panels
        // would box the video in a black frame).
        if (
          !phoneScaleTouch &&
          !(container as FullscreenElement).classList.contains(
            "experience-container--landscape",
          )
        ) {
          nativeHandleClick(event);
          return;
        }
        const fsEl = document.fullscreenElement || doc.webkitFullscreenElement;
        try {
          if (fsEl) {
            if (exitFs) {
              const pe = exitFs.call(document);
              if (pe && pe.catch) {
                pe.catch(() => {
                  /* ignore the rejection */
                });
              }
            }
          } else {
            const pr = (requestFs as Element["requestFullscreen"]).call(
              container,
              { navigationUI: "hide" },
            );
            if (pr && pr.then) {
              // Entering from portrait: once fullscreen is granted, lock the screen
              // to landscape so the device rotates. video.js's onFullscreenChange
              // also attempts this, but the explicit post-grant lock is what
              // reliably rotates on Android for a CONTAINER fullscreen. Best-effort —
              // desktop / unsupported browsers reject and are ignored.
              pr.then(
                () => {
                  try {
                    const o = (
                      typeof screen !== "undefined" ? screen.orientation : null
                    ) as LockableOrientation | null;
                    if (o && typeof o.lock === "function") {
                      const lp = o.lock("landscape");
                      if (lp && lp.catch) {
                        lp.catch(() => {
                          /* ignore the rejection */
                        });
                      }
                    }
                  } catch {
                    /* Screen Orientation lock unsupported — ignore */
                  }
                },
                () => {
                  /* no-op */
                },
              );
            }
          }
        } catch {
          nativeHandleClick(event);
        }
      };
    }

    // Mirror the native fullscreen state onto the player so the toggle icon flips.
    // Setting isFullscreen fires video.js's own 'fullscreenchange' →
    // `onFullscreenChange`, which already best-effort locks/unlocks orientation
    // to landscape — so this only needs to sync the state, not re-lock.
    self.onDocFullscreenChange = () => {
      const fsEl = document.fullscreenElement || doc.webkitFullscreenElement;
      if (self.player && !self.player.isDisposed()) {
        // Mirror only the CONTAINER fullscreen (the immersive button path) — video.js
        // doesn't otherwise know the container is fullscreen. When the fullscreen
        // element is the player's own el (desktop button / double-click → video.js's
        // native toggle), leave video.js to manage its own state; overwriting it
        // here would flip the toggle icon back to "enter" mid-fullscreen.
        // On exit (fsEl null) sync false for both paths.
        if (fsEl === container) {
          self.player.isFullscreen(true);
        } else if (!fsEl) {
          self.player.isFullscreen(false);
          // If the viewer left real fullscreen via an OS gesture (swipe / Done)
          // rather than our button, tear down the immersive layout too.
          if (window.__pixPseudoFs) {
            immersive.setImmersiveFullscreen(false);
          }
        }
      }
    };
    document.addEventListener("fullscreenchange", self.onDocFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      self.onDocFullscreenChange,
    );

    // Entering fullscreen from portrait swaps the layout (pseudo-fs on iOS, or the
    // rotate-into-landscape re-render on Android real fullscreen), which remounts
    // this player. The fresh toggle would show the "enter" icon mid-fullscreen —
    // sync it on mount from BOTH the pseudo-fs flag and the live real-fullscreen
    // state (container is the fullscreen element).
    const inRealFs =
      (document.fullscreenElement || doc.webkitFullscreenElement) === container;
    if ((window.__pixPseudoFs || inRealFs) && !self.player.isDisposed()) {
      self.player.isFullscreen(true);
    }
  }

  // The detach part for fullscreen: the document listeners, then the toggle's
  // native handleClick, so the next mount re-wraps from the original, never
  // from this mount's wrapper.
  function detachFullscreen() {
    if (self.onDocFullscreenChange) {
      document.removeEventListener(
        "fullscreenchange",
        self.onDocFullscreenChange,
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        self.onDocFullscreenChange,
      );
      self.onDocFullscreenChange = null;
    }
    // Skipped on a disposed player: a chain advance / true teardown disposes the
    // persistent player in Experience's cleanup, which runs before this one, so
    // the control bar is disposed-but-truthy — getChild() would read its nulled
    // child index and throw. There is nothing to restore on a disposed player.
    if (self.player && !self.player.isDisposed() && self.player.controlBar) {
      const fsToggle = self.player.controlBar.getChild("fullscreenToggle") as
        | FullscreenToggle
        | undefined;
      if (fsToggle && fsToggle._pixNativeHandleClick) {
        fsToggle.handleClick = fsToggle._pixNativeHandleClick;
      }
    }
  }

  // YouTube-style: entering fullscreen from portrait rotates the device to
  // landscape (Screen Orientation lock), and exiting releases the lock.
  // Best-effort — unsupported on iOS Safari and desktop, so guarded.
  function onFullscreenChange() {
    trackFullscreenEnter();
    const isFs = !!(self.player && self.player.isFullscreen());
    // Clear the auto-rotate flag whenever fullscreen exits (by rotation, OS
    // gesture, or button), so a later button-driven fullscreen locks normally.
    if (!isFs) {
      window.__pixAutoFs = false;
    }
    try {
      const orientation = (
        typeof screen !== "undefined" ? screen.orientation : null
      ) as LockableOrientation | null;
      if (!orientation || typeof orientation.lock !== "function") {
        return;
      }
      if (isFs) {
        // Fullscreen auto-entered from a device rotation must NOT lock to
        // landscape — the lock blocks the physical rotate back to portrait that is
        // the exit trigger. The button path (portrait -> fullscreen) still locks so
        // the device turns to landscape.
        if (window.__pixAutoFs) {
          return;
        }
        const p = orientation.lock("landscape");
        if (p && typeof p.catch === "function") {
          p.catch(() => {
            /* ignore the rejection */
          });
        }
      } else if (typeof orientation.unlock === "function") {
        orientation.unlock();
      }
    } catch {
      /* Screen Orientation lock unsupported — ignore */
    }
  }

  // Our substitute for video.js's default double-click handler (wired via
  // userActions.doubleClick). video.js has already applied its control-bar /
  // modal guard, so this only runs for a "real" double-click on the video. Record
  // the method and replicate the default fullscreen toggle; the Fullscreen Double
  // Click event fires on the resulting enter (trackFullscreenEnter), so exits are
  // never counted. (PiP is disabled here, so no PiP branch is needed.)
  function onDoubleClickFullscreen() {
    if (!self.player) {
      return;
    }
    self.fsEnterMethod = "double_click";
    if (self.player.isFullscreen()) {
      self.player.exitFullscreen();
    } else {
      self.player.requestFullscreen();
    }
  }

  // Emit a fullscreen-enter event exactly once per successful enter, attributed
  // to how it was triggered (button vs double-click). Fires for both the desktop
  // native fullscreen and the immersive container fullscreen — both surface
  // through the player's fullscreenchange. Experience id + player context
  // (device, dimensions) are auto-attached by analytics.track.
  function trackFullscreenEnter() {
    const isFs = !!(
      self.player &&
      !self.player.isDisposed() &&
      self.player.isFullscreen()
    );
    if (isFs && !self.fsWasActive) {
      if (self.fsEnterMethod === "button") {
        analytics.track("Fullscreen Button Clicked");
      } else if (self.fsEnterMethod === "double_click") {
        analytics.track("Fullscreen Double Click");
      } else if (typeof window !== "undefined" && window.__pixAutoFs) {
        // Auto-entered from a device rotation (no button/double-click), so
        // product can see how often the rotate path actually fires.
        analytics.track("Fullscreen Auto Rotate");
      }
    }
    if (!isFs) {
      self.fsEnterMethod = null;
    }
    self.fsWasActive = isFs;
  }

  return {
    setupFullscreen,
    detachFullscreen,
    onFullscreenChange,
    onDoubleClickFullscreen,
  };
}

export type VideoFullscreen = ReturnType<typeof createVideoFullscreen>;

/** The fullscreen wiring of one VideoJs mount; stable for the mount. */
export function useVideoFullscreen(self: VideoJsSelf): VideoFullscreen {
  const [fullscreen] = useState(() => createVideoFullscreen(self));
  return fullscreen;
}
